// Opening rows: games grouped by opening and colour, with results and the
// average score after the opening. The building block of the opening cards
// (by family) and the openings screen's ranking (by variation).
import { average, type Accumulator } from '@chess/api-model';
import { emptyResults } from './results.ts';
import type { OpeningRow, OpeningsRanking } from './types.ts';
import { type GameFacts } from '@chess/api-model';

/** How many openings an opening card lists. */
export const TOP_OPENINGS = 5;

/** What an opening row groups by: the family (the home cards) or the variation (the openings screen). */
export type OpeningGrouping = 'family' | 'variation';

/** The name a game is grouped under; a variation falls back to the family when the game has none. */
const openingOf = (g: GameFacts, by: OpeningGrouping) => (by === 'variation' ? (g.variation ?? g.opening) : g.opening);

/** One row per opening and colour, with results and the average opening score; in first-seen order. */
export class OpeningRowsAccumulator implements Accumulator<OpeningRow[]> {
  private readonly rows = new Map<string, OpeningRow & { scores: number[] }>();
  private readonly by: OpeningGrouping;

  constructor(by: OpeningGrouping = 'family') {
    this.by = by;
  }

  add(g: GameFacts): void {
    const opening = openingOf(g, this.by);
    if (!opening) return;
    const key = `${opening}|${g.colour}`;
    let row = this.rows.get(key);
    if (!row) {
      row = { opening, colour: g.colour, games: 0, results: emptyResults(), scanned: 0, scores: [] };
      if (this.by === 'variation' && g.opening) row.family = g.opening;
      this.rows.set(key, row);
    }
    row.games += 1;
    row.results[g.outcome] += 1;
    if (g.scanned && g.openingScore !== undefined) {
      row.scanned += 1;
      row.scores.push(g.openingScore);
    }
  }

  result(): OpeningRow[] {
    return [...this.rows.values()].map(({ scores, ...row }) => ({ ...row, averageScore: average(scores) }));
  }
}

/** One row per opening and colour of the given games. */
export function openingRows(games: Iterable<GameFacts>, by: OpeningGrouping = 'family'): OpeningRow[] {
  const rows = new OpeningRowsAccumulator(by);
  for (const g of games) rows.add(g);
  return rows.result();
}

/** Most games first, then the name: the order of a list of openings by how often they were played. */
export const byGamesThenName = (a: OpeningRow, b: OpeningRow): number => b.games - a.games || a.opening.localeCompare(b.opening);

/** Scanned games an opening needs to be ranked on the openings screen. */
export const RANKED_MIN_GAMES = 3;

/**
 * Every opening variation and colour of the given games, by the score they leave
 * the reader with; the game's result does not count. Openings with at least
 * `RANKED_MIN_GAMES` scanned games are listed by games played, most first,
 * with their average score after the opening (ties: the worse score, then the
 * name); those played that often but scanned less wait for a scan; those
 * played less are set apart.
 */
export function openingsRanking(games: GameFacts[]): OpeningsRanking {
  const rows = openingRows(games, 'variation');
  const byName = (a: OpeningRow, b: OpeningRow) => a.opening.localeCompare(b.opening) || a.colour.localeCompare(b.colour);
  const byGames = (a: OpeningRow, b: OpeningRow) => b.games - a.games || byName(a, b);
  const scored = rows.filter((r) => r.scanned >= RANKED_MIN_GAMES).sort((a, b) => b.games - a.games || a.averageScore! - b.averageScore! || byName(a, b));
  const unscanned = rows.filter((r) => r.scanned < RANKED_MIN_GAMES && r.games >= RANKED_MIN_GAMES).sort(byGames);
  const tooFew = rows.filter((r) => r.games < RANKED_MIN_GAMES).sort(byGames);
  const order = new Map(unscanned.map((r, i) => [`${r.opening}|${r.colour}`, i]));
  const keyOf = (g: GameFacts) => `${openingOf(g, 'variation')}|${g.colour}`;
  const unscannedGames = games
    .filter((g) => openingOf(g, 'variation') && order.has(keyOf(g)) && !(g.scanned && g.openingScore !== undefined))
    .sort((a, b) => order.get(keyOf(a))! - order.get(keyOf(b))! || b.playedAt - a.playedAt)
    .map((g) => g.id);
  return { scored, unscanned, tooFew, unscannedGames };
}
